import {createContext, use, useMemo, useState, type ReactNode} from 'react';
import {createPortal} from 'react-dom';
import {useLocation} from 'react-router-dom';
import {SidebarTrigger, useSidebar} from '@kroll-resolver-crisp/ui-components-react';
import {pageTitle} from '@/lib/documentTitle';

// The design system mandates a page title on every screen and makes it part of the shell rather
// than something a page remembers to render. The title comes from the route; a page contributes
// only its right-hand actions, through `PageHeaderActions`, which portals them into the header's
// slot so the header itself stays fixed.
type Slot = {
  readonly node: HTMLElement | null;
  readonly setNode: (node: HTMLElement | null) => void;
};

const PageHeaderSlotContext = createContext<Slot | null>(null);

export function PageHeaderProvider({children}: {readonly children: ReactNode}) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const slot = useMemo(() => ({node, setNode}), [node]);
  return <PageHeaderSlotContext value={slot}>{children}</PageHeaderSlotContext>;
}

export function PageHeader() {
  const {pathname} = useLocation();
  const slot = use(PageHeaderSlotContext);
  const {isMobile} = useSidebar();

  return (
    <header className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-3">
      {/* On desktop the collapse trigger lives in the sidebar's AppSwitcher row. Under 768px the
          sidebar is a closed drawer, so the switcher cannot open it: the header carries the
          hamburger ("Open menu") at touch size, as the design's mobile template does. */}
      {isMobile ? <SidebarTrigger className="-ml-1.5 size-11 [&_svg]:size-[22px]!" /> : null}
      <h1 className="font-heading truncate text-sm font-medium">{pageTitle(pathname) ?? 'PRI'}</h1>
      <div ref={slot?.setNode} className="ml-auto flex min-w-0 items-center gap-2" />
    </header>
  );
}

// Renders its children in the page header's right-hand slot. Pages that show media put their
// blur-thumbnails toggle here; list pages put their scope toggle and primary action here.
export function PageHeaderActions({children}: {readonly children: ReactNode}) {
  const slot = use(PageHeaderSlotContext);
  if (!slot?.node) {
    return null;
  }

  return createPortal(children, slot.node);
}
